import { publicApiClient, privateApiClient } from './apiClient';
import { authenticateUser } from './oauth';
import { DEFAULT_COUNTRY_CODE, storageKeys } from '../config/environment';
import { normalizeCompanies } from '../utils/organizationRoleUtils';
import { toEmailString } from '../utils/toEmailString';

const normalizePhone = (phone, countryCode = DEFAULT_COUNTRY_CODE) => {
    const sanitized = phone.replace(/\D/g, '');
    if (!sanitized) {
        throw new Error('Veuillez saisir un numéro de téléphone.');
    }
    return `+${countryCode}${sanitized.replace(/^0+/, '')}`;
};

/**
 * Extrait le numéro de téléphone d'un objet phone ou d'une string
 * @param {string|object} phone - Le téléphone (string ou objet avec fullNumber/number)
 * @returns {string} - Le numéro de téléphone sous forme de string
 */
export const extractPhoneNumber = (phone) => {
    if (!phone) return '';
    if (typeof phone === 'string') return phone;
    if (typeof phone === 'object') {
        return phone.fullNumber || phone.number || '';
    }
    return '';
};

const persistOrganisationData = (organisation, solutionId, phone, organizations) => {
    localStorage.setItem(storageKeys.organisation, JSON.stringify(organisation));
    localStorage.setItem(storageKeys.destination, organisation.id);
    localStorage.setItem(storageKeys.solution, solutionId);
    localStorage.setItem(storageKeys.userPhone, phone);
    localStorage.setItem(storageKeys.organizations, JSON.stringify(organizations));
};

export const loginWithPhonePin = async ({ phone, pin, countryCode = DEFAULT_COUNTRY_CODE }) => {
    if (!pin) {
        throw new Error('Veuillez saisir votre code PIN.');
    }

    const msisdn = normalizePhone(phone, countryCode);

    const phoneResponse = await publicApiClient.get(`/usr/register/check-phone/${msisdn}`);
    if (phoneResponse?.data?.error) {
        throw new Error(phoneResponse.data.message || 'Numéro de téléphone invalide.');
    }

    await authenticateUser(msisdn, pin);

    const solutionsResponse = await publicApiClient.get('/usr/solutions', {
        params: { code: 'POS' }
    });
    const solution = solutionsResponse?.data;
    if (!solution?.id) {
        throw new Error('Solution POS introuvable pour ce compte.');
    }

    const companiesResponse = await privateApiClient.get('/usr/companies', {
        params: {
            cat: 'all',
            sol: solution.id,
            slug: '',
            pge: 1,
            lmt: 100
        }
    });

    const companies = companiesResponse?.data?.contents || [];
    if (!companies.length) {
        throw new Error("Aucune organisation n'est associée à ce compte.");
    }

    const normalizedCompanies = normalizeCompanies(companies);
    persistOrganisationData(companies[0], solution.id, msisdn, normalizedCompanies);

    const userSessionResponse = await privateApiClient.get('/usr/users/session');
    const profile = userSessionResponse?.data || {};

    // Extraire le numéro de téléphone si c'est un objet
    const phoneNumber = extractPhoneNumber(profile.phone);

    return {
        user: {
            id: profile.id,
            username: profile.username || phoneNumber || msisdn,
            name: profile.fullName || profile.name || profile.username || phoneNumber || msisdn,
            email: toEmailString(profile.email)
        },
        organizations: normalizedCompanies
    };
};

